<template>
    <div ref="selectField" v-on-click-outside="close" :dusk="props.dusk" class="forms__select__wrapper">
        <!--
            Dummy input element
        -->
        <input
            :id="props.id"
            type="text"
            class="forms__select__hidden-input"
            autocomplete="chrome-off"
            :tabindex="props.tabindex"
            @focusin="open"
            @focusout="applyIfFocusIsNotWithin(delayedToggle)"
        />

        <!--
            Current value
        -->
        <Float
            placement="bottom"
            :offset="6"
            :middleware="floatMiddleware"
            composable
            :portal="portal"
            enter="transition duration-200 ease-out"
            enter-from="opacity-0 -translate-y-1"
            enter-to="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 -translate-y-1"
            :show="opened"
        >
            <FloatReference>
                <div
                    :dusk="`${props.dusk}-current-value`"
                    class="forms__field_input--select"
                    :class="[
                        $attrs.class,
                        'forms__select__field-for-typing',
                        { 'forms__field_input--select--flex': props.variant !== 'inline' },
                        { 'forms__field_input--select--inline': props.variant === 'inline' },
                        sizeClass,
                        { 'forms__field_input--select--opened': opened },
                        { 'forms__field_input--select--disabled': props.disabled },
                        { 'forms__field_input--select--enabled': !props.disabled },
                    ]"
                    @click="handleTriggerClick"
                >
                    <!-- Selected value -->
                    <slot name="currentValue" :option="currentValue" :search="search">
                        <span
                            v-if="typeof currentValue === 'object'"
                            class="forms__select__current-value--single"
                            :class="[{ 'forms__select__current-value--single--no-selected': !hasSelected }]"
                        >
                            {{ currentValue[props.label] ? currentValue[props.label] : currentValue[props.property] }}
                        </span>
                        <span
                            v-else
                            class="forms__select__current-value--multiple"
                            :class="[{ 'forms__select__current-value--multiple--no-selected ': !hasSelected }]"
                        >
                            <div
                                v-if="props.multiple && props.removable && !props.orderable"
                                class="forms__select__current-value--multiple__items"
                            >
                                <template v-for="entry in selected" :key="entry[props.property] ?? entry">
                                    <slot name="selectedItem" :option="entry" :remove="() => remove(entry)">
                                        <span
                                            class="forms__select__current-value--multiple__item-single"
                                            :class="labelSizeClass"
                                        >
                                            <span>
                                                {{ entry[props.label] ?? entry.id }}
                                            </span>
                                            <button
                                                type="button"
                                                class="forms__select__current-value--multiple__item-single__action"
                                                @click.stop="remove(entry)"
                                            >
                                                <icon
                                                    icon="hero/outline/x"
                                                    class="forms__select__current-value--multiple__item-single__icon"
                                                ></icon>
                                            </button>
                                        </span>
                                    </slot>
                                </template>
                            </div>
                            <div v-else-if="props.multiple && props.orderable">
                                {{
                                    selectedCount > 0
                                        ? lang('fields::fields.select.selected_count', { count: selectedCount })
                                        : currentValue
                                }}
                            </div>
                            <div v-else>{{ currentValue }}</div>
                        </span>
                    </slot>
                    <span v-if="props.valid" class="forms__select__current-value__feedback">
                        <icon icon="hero/outline/check" class="forms__select__current-value__feedback--valid"></icon>
                    </span>
                    <span v-if="props.invalid" class="forms__select__current-value__feedback">
                        <icon
                            icon="hero/outline/exclamation-circle"
                            class="forms__select__current-value__feedback--invalid"
                        ></icon>
                    </span>
                    <span class="forms__select__current-value__chevron-wrapper">
                        <icon icon="hero/outline/chevron-down" class="forms__select__current-value__chevron"></icon>
                    </span>
                </div>
            </FloatReference>

            <!--
            Options
            -->
            <FloatContent>
                <ui-popover>
                    <!-- Options: Search -->
                    <ui-popover-section v-if="searchable">
                        <ui-popover-header>
                            <AdminText
                                ref="searchField"
                                v-model="search"
                                :size="size"
                                :placeholder="lang('common::common.search_placeholder')"
                            />
                        </ui-popover-header>
                    </ui-popover-section>

                    <!-- Options: Before -->
                    <slot name="optionsBefore"> </slot>

                    <!-- Options -->
                    <ui-popover-segment>
                        <ui-scrollable class="max-h-80">
                            <template v-if="Array.isArray(filteredOptions)">
                                <ui-popover-link
                                    v-for="(option, optionIndex) in filteredOptions"
                                    :key="option"
                                    :dusk="`${props.dusk}-option-${option[props.property]}`"
                                    :clickable="true"
                                    :selected="optionIndex === pointerIndex"
                                    :active="isSelected(option)"
                                    @click="select(option)"
                                >
                                    <slot name="option" :option="option" :search="search">
                                        {{ option[props.label] ? option[props.label] : option[props.property] }}
                                    </slot>
                                </ui-popover-link>
                            </template>
                            <template v-else>
                                <ui-popover-segment v-for="(group, groupTitle) in filteredOptions" :key="groupTitle">
                                    <ui-popover-header>
                                        <ui-popover-headline> {{ groupTitle }} </ui-popover-headline>
                                    </ui-popover-header>

                                    <ui-popover-link
                                        v-for="(option, optionIndex) in group"
                                        :key="option"
                                        :dusk="`${props.dusk}-option-${option[props.property]}`"
                                        :clickable="true"
                                        :selected="optionIndex === pointerIndex"
                                        :active="isSelected(option) || isSelectedMultiple(option)"
                                        @click="select(option)"
                                    >
                                        <slot name="option" :option="option" :search="search">
                                            {{ option[props.label] ? option[props.label] : option[props.property] }}
                                        </slot>
                                    </ui-popover-link>
                                </ui-popover-segment>
                            </template>
                        </ui-scrollable>
                    </ui-popover-segment>

                    <!-- Options: After -->
                    <slot name="optionsAfter"> </slot>
                </ui-popover>
            </FloatContent>
        </Float>

        <!-- Orderable list for multiple selection -->
        <div v-if="props.multiple && props.orderable && selectedCount > 0" class="forms__select__orderable-list">
            <Sortable :list="orderableSelected" :item-key="props.property" @start="onSortStart" @end="onSortEnd">
                <template #item="{ element }">
                    <div class="forms__select__orderable-item">
                        <!-- Drag handle - must have cursor-grab class for Sortable handle selector -->
                        <div class="forms__select__orderable-item__handle cursor-grab">
                            <Icon
                                icon="phosphor/regular/dots-six-vertical"
                                class="forms__select__orderable-item__handle-icon cursor-grab"
                            />
                        </div>
                        <span class="forms__select__orderable-item__label">
                            {{ element[props.label] ?? element[props.property] }}
                        </span>
                        <button type="button" class="forms__select__orderable-item__remove" @click="remove(element)">
                            <Icon icon="phosphor/regular/x" class="forms__select__orderable-item__remove-icon" />
                        </button>
                    </div>
                </template>
            </Sortable>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue';
import type { Ref } from 'vue';
import { lang } from '@/lang/composable';
import { useFocusWithin } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import Icon from '@src/Common/Components/Icon.vue';
import Sortable from '@src/Common/Components/Sortable.vue';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';
import { size as floatSize } from '@floating-ui/dom';
import type { FieldComponent } from '@src/Common/Components/types';

defineOptions({
    inheritAttrs: false,
});

type SelectedValue = null | string | number | object | string[] | number[] | object[];

const emit = defineEmits<{
    (event: 'update:modelValue', content: SelectedValue): void;
    (event: 'change', content: SelectedValue): void;
    (event: 'search', content: string): void;
}>();

const props = withDefaults(defineProps<FieldComponent>(), {
    modelValue: null,
    options: () => [],
    placeholder: undefined,
    property: 'id',
    size: 'base',
    dusk: null,
    label: 'name',
    opened: false,
    valid: false,
    invalid: false,
    variant: null,
    disabled: false,
    multiple: false,
    orderable: false,
    removable: true,
    searchable: false,
    tabindex: null,
    portal: false,
});

const opened: Ref<boolean> = ref(props.opened ?? false);

const selected = toRef(props, 'modelValue');

const selectField: Ref<HTMLElement | null> = ref(null);
const { focused } = useFocusWithin(selectField);

const search: Ref<string> = ref('');
const searchField: Ref<HTMLInputElement | null> = ref(null);

const size: Ref<string> = ref(props.size);

const floatMiddleware = [
    floatSize({
        apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
                width: `${rects.reference.width}px`,
            });
        },
    }),
];

const handleTriggerClick = (event: MouseEvent): void => {
    if (props.portal) {
        event.stopPropagation();
    }
    toggle();
};

const toggle = (): void => {
    if (opened.value) {
        close();
    } else {
        open();
    }
};

const applyIfFocusIsNotWithin = (callback: () => void): void => {
    setTimeout(() => {
        if (!focused.value) {
            callback();
        }
    }, 1);
};

const delayedToggle = (): void => {
    setTimeout(() => {
        toggle();
    }, 200);
};

const open = (): void => {
    opened.value = true;

    setTimeout(() => {
        searchField.value?.focus();
    }, 1);
};

const close = (): void => {
    opened.value = false;
};

const select = (option: any): void => {
    // In orderable mode with multiple selection, work with orderableSelected
    if (props.multiple && props.orderable) {
        const items = [...orderableSelected.value];

        if (isSelectedMultiple(option)) {
            // Remove item
            const found = items.findIndex((single) => single[props.property] === option[props.property]);
            if (found !== -1) {
                items.splice(found, 1);
            }
        } else {
            // Add item at the end
            items.push(option);
        }

        orderableSelected.value = items;
        emit('update:modelValue', prepare([...items]));
        emit('change', prepare([...items]));
        opened.value = false;
        return;
    }

    // Non-orderable mode
    let value: SelectedValue = selected.value;

    if (props.multiple) {
        if (isSelectedMultiple(option)) {
            const found = value.findIndex((single) => single[props.property] === option[props.property]);
            value = value.slice(0);
            value.splice(found, 1);
        } else {
            if (!value) {
                value = [];
            }

            if (!Array.isArray(value)) {
                value = [];
            }

            value.push(option);
        }
    } else {
        if (props.property) {
            value = option[props.property];
        } else {
            value = option;
        }
    }

    emit('update:modelValue', prepare(value));
    emit('change', prepare(value));
    opened.value = false;
};

const remove = (option: any): void => {
    if (!props.multiple) {
        return;
    }

    // In orderable mode, work with orderableSelected to preserve drag order
    if (props.orderable) {
        let items = [...orderableSelected.value];

        if (props.property) {
            items = items.filter(
                (item) => item != option[props.property] && item[props.property] != option[props.property],
            );
        } else {
            items = items.filter((item) => item != option);
        }

        orderableSelected.value = items;
        emit('update:modelValue', prepare([...items]));
        emit('change', prepare([...items]));
        return;
    }

    // Non-orderable mode - work with selected value
    let value: SelectedValue = selected.value;

    if (!selected.value) {
        return;
    }

    if (!Array.isArray(selected.value)) {
        return;
    }

    if (props.property) {
        value = value.filter(
            (item) => item != option[props.property] && item[props.property] != option[props.property],
        );
    } else {
        value = value.filter((item) => item != option);
    }

    emit('update:modelValue', prepare(value));
    emit('change', prepare(value));
};

const prepare = (value: SelectedValue): SelectedValue => {
    if (!props.multiple) {
        return value;
    }

    if (!value) {
        return [];
    }

    if (!Array.isArray(value)) {
        return [];
    }

    if (props.property) {
        return value.map((item) => {
            // if (typeof item === 'object') {
            //     return item[props.property];
            // }

            return item;
        });
    }

    return value;
};

/**
 * Check if the option is selected
 * (applies to single select)
 *
 * @param option
 */
const isSelected = (option: any): boolean =>
    (selected.value !== null && option[props.property] === selected.value) ||
    (selected.value !== null && option[props.property] === parseInt(selected.value)) ||
    (option[props.property] === null && !selected.value);

/**
 * Check if the option is one of selected options
 * (applies to multiple select)
 *
 * @param option
 */
const isSelectedMultiple = (option: unknown): boolean => {
    // In orderable mode, check orderableSelected for accurate state after reordering
    const checkArray = props.orderable ? orderableSelected.value : selected.value;

    if (!checkArray) {
        return false;
    }

    if (!Array.isArray(checkArray)) {
        return false;
    }

    if (checkArray.filter((item) => item === option[props.property]).length > 0) {
        return true;
    }

    if (option[props.property] === null && checkArray.length === 0) {
        return true;
    }

    if (typeof checkArray.find((item) => item[props.property] === option[props.property]) !== 'undefined') {
        return true;
    }

    return false;
};

const hasSelectedSingle = (): boolean => {
    const options = Array.isArray(props.options) ? props.options : Object.values(props.options).flat();
    const found = options.find((item) => isSelected(item));

    if (found) {
        return true;
    }

    return false;
};

const hasSelectedMultiple = (): boolean => {
    const options = Array.isArray(props.options) ? props.options : Object.values(props.options).flat();
    return props.multiple && options.filter((item) => isSelectedMultiple(item)).length > 0;
};

const hasSelected = computed((): boolean => {
    return hasSelectedSingle() || hasSelectedMultiple();
});

const selectedCount = computed((): number => {
    if (!props.multiple) {
        return 0;
    }

    // In orderable mode, use orderableSelected for accurate count after reordering
    if (props.orderable) {
        return orderableSelected.value.length;
    }

    if (!selected.value || !Array.isArray(selected.value)) {
        return 0;
    }
    return selected.value.length;
});

/**
 * Orderable selected items (mutable ref for Sortable).
 * Sortable mutates this array directly during drag operations.
 */
const orderableSelected: Ref<any[]> = ref([]);
const isSorting: Ref<boolean> = ref(false);
let orderAtDragStart: string = '';

watch(
    selected,
    (value) => {
        // Don't sync while sorting - Sortable is managing the order
        if (isSorting.value) {
            return;
        }
        if (props.orderable && Array.isArray(value)) {
            orderableSelected.value = [...value];
        }
    },
    { immediate: true },
);

/**
 * Handle sort start - store current order and prevent watch from interfering.
 *
 * @return void No return expected.
 */
const onSortStart = (): void => {
    orderAtDragStart = JSON.stringify(orderableSelected.value);
    isSorting.value = true;
};

/**
 * Handle sort end event from Sortable component.
 * Checks if sortablejs-vue3 already mutated the array, otherwise does manual reorder.
 *
 * @param event Sort event with oldIndex and newIndex.
 * @return void No return expected.
 */
const onSortEnd = (event: { oldIndex: number; newIndex: number }): void => {
    if (!props.orderable) {
        isSorting.value = false;
        return;
    }

    const currentOrder = JSON.stringify(orderableSelected.value);
    const wasMutatedBySortable = orderAtDragStart !== currentOrder;

    let items: any[];

    if (wasMutatedBySortable) {
        // sortablejs-vue3 already mutated the array, use it directly
        items = [...orderableSelected.value];
    } else {
        // sortablejs-vue3 did NOT mutate the array, do manual reorder
        items = [...orderableSelected.value];
        const [movedItem] = items.splice(event.oldIndex, 1);
        items.splice(event.newIndex, 0, movedItem);
        orderableSelected.value = items;
    }

    emit('update:modelValue', items);
    emit('change', items);

    isSorting.value = false;
};

const currentValue = computed(() => {
    const options = Array.isArray(props.options) ? props.options : Object.values(props.options).flat();
    if (props.multiple) {
        // When the selected value is an array
        const found = options
            .filter((item) => isSelectedMultiple(item))
            .map((item) => item[props.label])
            .join(', ');

        if (found) {
            return found;
        }
    } else {
        // When the selected value is a single value
        const found = options.find((item) => isSelected(item));
        if (found) {
            return found;
        }
    }

    // When there is no selected value
    return props.placeholder ? props.placeholder : lang('general.select.placeholder');
});

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'forms__select--xs ',
        sm: 'forms__select--sm ',
        lg: 'forms__select--lg ',
        xl: 'forms__select--xl ',
        base: 'forms__select--base ',
    };

    return sizes[size.value] ?? sizes.base;
});

const filteredOptions = computed(() => {
    if (!props.searchable) {
        return props.options;
    }

    if (search.value === '') {
        return props.options;
    }

    return props.options.filter((item) => {
        if (typeof item === 'object') {
            return item[props.label].toLowerCase().includes(search.value.toLowerCase());
        }

        return item.toLowerCase().includes(search.value.toLowerCase());
    });
});

const labelSizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'forms__select__label--xs',
        sm: 'forms__select__label--xs',
        base: 'forms__select__label--sm',
        lg: 'forms__select__label--base',
        xl: 'forms__select__label--lg',
    };

    return `${sizes[props.size]}`;
});

/**
 * Enable arrow navigation
 */
const pointerIndex: Ref<number> = ref(-1);

watch(opened, (value) => {
    if (value) {
        pointerIndex.value = -1;
        activateKeyboardNavigation();
    } else {
        deactivateKeyboardNavigation();
    }
});

const activateKeyboardNavigation = (): void => {
    document.addEventListener('keydown', arrowNavigation);
};

const deactivateKeyboardNavigation = (): void => {
    document.removeEventListener('keydown', arrowNavigation);
};

const arrowNavigation = (event: KeyboardEvent): void => {
    const options = Array.isArray(filteredOptions.value) ? filteredOptions.value : [];

    if (event.key === 'ArrowDown') {
        event.preventDefault();
        pointerIndex.value = pointerIndex.value < options.length - 1 ? pointerIndex.value + 1 : 0;
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        pointerIndex.value = pointerIndex.value > 0 ? pointerIndex.value - 1 : options.length - 1;
    }

    if (event.key === 'Enter') {
        event.preventDefault();
        if (pointerIndex.value >= 0 && pointerIndex.value < options.length) {
            select(options[pointerIndex.value]);
        }
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        close();
    }
};

watch(search, (value) => {
    emit('search', value);
});

watch(
    () => props.size,
    (value) => {
        if (value) {
            size.value = value;
        }
    },
);
</script>
